<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import type { PageDefinition } from '@/types/page'
import AppIcon from '@/components/AppIcon.vue'

const props = defineProps<{
  open: boolean
  definition: PageDefinition
  hiddenKeys: string[]
}>()

const emit = defineEmits<{
  close: []
  apply: [keys: string[]]
}>()

const checked = ref<string[]>([])

const columns = computed(() => props.definition.columns)

watch(() => props.open, open => {
  if (!open) return
  checked.value = columns.value.filter(column => !props.hiddenKeys.includes(column.key)).map(column => column.key)
})

function resetRecommended(): void {
  checked.value = columns.value.filter(column => !column.hideByDefault).map(column => column.key)
}

function showAll(): void {
  checked.value = columns.value.map(column => column.key)
}

function apply(): void {
  const hidden = columns.value.filter(column => !checked.value.includes(column.key)).map(column => column.key)
  emit('apply', hidden)
  emit('close')
}
</script>

<template>
  <el-dialog :model-value="open" width="430px" align-center :show-close="false" class="columns-dialog" @close="emit('close')">
    <template #header>
      <div class="columns-dialog__head">
        <div>
          <h2>列表字段</h2>
          <p>按当前业务需要选择列，操作列始终保留；配置仅在本次原型会话生效。</p>
        </div>
        <button type="button" class="icon-button" aria-label="关闭" @click="emit('close')"><AppIcon name="close" :size="17" /></button>
      </div>
    </template>
    <div class="columns-dialog__tools">
      <button type="button" class="btn btn--sm" @click="resetRecommended">恢复推荐</button>
      <button type="button" class="btn btn--sm" @click="showAll">显示全部</button>
      <span>{{ checked.length }}/{{ columns.length }} 列</span>
    </div>
    <el-checkbox-group v-model="checked" class="columns-dialog__list">
      <label v-for="column in columns" :key="column.key" class="columns-dialog__item">
        <el-checkbox :value="column.key">{{ column.label }}</el-checkbox>
        <span v-if="!column.hideByDefault" class="columns-dialog__tag">推荐</span>
        <span v-else class="columns-dialog__tag columns-dialog__tag--muted">扩展</span>
      </label>
    </el-checkbox-group>
    <template #footer>
      <div class="columns-dialog__foot">
        <button type="button" class="btn" @click="emit('close')">取消</button>
        <button type="button" class="btn btn--primary" @click="apply">应用字段</button>
      </div>
    </template>
  </el-dialog>
</template>


<style scoped>
.columns-dialog__head { display: flex; align-items: flex-start; gap: 10px; }
.columns-dialog__head h2 { margin: 0; font-size: 16px; color: var(--c-ink); }
.columns-dialog__head p { margin: 4px 0 0; font-size: 10.5px; color: var(--c-muted); line-height: 1.55; }
.columns-dialog__head .icon-button { margin-left: auto; }
.columns-dialog__tools { display: flex; align-items: center; gap: 6px; padding-bottom: 10px; border-bottom: 1px solid var(--c-divider); }
.columns-dialog__tools span { margin-left: auto; font-size: 10px; color: var(--c-muted); }
.columns-dialog__list { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 2px 8px; max-height: 360px; overflow: auto; padding-top: 8px; }
.columns-dialog__item { display: flex; align-items: center; gap: 4px; min-width: 0; }
.columns-dialog__tag { font-size: 8px; color: var(--c-brand-strong); background: var(--c-brand-soft); border-radius: 4px; padding: 1px 4px; }
.columns-dialog__tag--muted { color: var(--c-muted); background: var(--c-surface-muted); }
.columns-dialog__foot { display: flex; justify-content: flex-end; gap: 8px; }
</style>
